import { gsap } from "gsap";
import {
  AreaSeries,
  ColorType,
  createChart,
  CrosshairMode,
  LastPriceAnimationMode,
  LineType,
  PriceLineSource,
  type Time,
} from "lightweight-charts";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useGetHistoryChartLite } from "@/market_page/component/trade/market/hooks/ueGetHistoryChartLite";
import { usePairs } from "@/market_page/component/trade/store/pairs";
import { usePrice } from "@/market_page/component/trade/store/price";
import { PartialPriceLine } from "../partial-price-line";

const opts = {
  colors: {
    backgroundColor: "rgba(0, 102, 255, 0)",
    lineColor: "#0066FF",
    textColor: "rgba(0, 102, 255, 0)",
    areaTopColor: "rgba(0, 102, 255, 0.60)",
    areaBottomColor: "rgba(0, 102, 255, 0.00)",
  },
};

const Chart = () => {
  const [searchParams] = useSearchParams();

  const pairIndex =
    searchParams.get("pairIndex") ?? sessionStorage.getItem("pairIndex") ?? "0";
  const chartContainerRef = useRef<HTMLDivElement | null>(null);

  const pairs = usePairs(useShallow((state) => state.data));
  const pair = pairs.find((item) => item.pairIndex === Number(pairIndex));

  const { data: historyChart, isLoading } = useGetHistoryChartLite(
    pair?.symbol ?? "",
    pair?.historyCall
  );

  useEffect(() => {
    if (!chartContainerRef.current || !pair || !historyChart) {
      return;
    }

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: {
          type: ColorType.Solid,
          color: opts.colors.backgroundColor,
        },
        textColor: opts.colors.textColor,
      },
      rightPriceScale: { borderVisible: false, textColor: "#4C4C4C" },
      width: chartContainerRef.current.clientWidth,
      height: 540,
      autoSize: true,
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false },
      },
      timeScale: {
        visible: false,
        borderVisible: false,
        minBarSpacing: 0.1,
        maxBarSpacing: 4,
        fixLeftEdge: true,
        fixRightEdge: true,
        barSpacing: 4,
        ticksVisible: true,
        rightOffset: 8,
        tickMarkFormatter: (time: string) =>
          `${new Date(time).getHours()}:${new Date(time).getSeconds()}`,
      },
      leftPriceScale: { visible: false },
      crosshair: { mode: CrosshairMode.Hidden },
      handleScroll: {
        vertTouchDrag: false,
        horzTouchDrag: true,
      },
    });

    const newSeries = chart.addSeries(AreaSeries, {
      lineColor: opts.colors.lineColor,
      priceFormat: {
        type: "price",
        precision: pair.decimalsToShow ?? 2, // số chữ số sau dấu thập phân
        minMove: 1 / Math.pow(10, pair.decimalsToShow ?? 2),
      },
      topColor: opts.colors.areaTopColor,
      bottomColor: opts.colors.areaBottomColor,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lineType: LineType.Curved,
      lineWidth: 2,
      priceLineSource: PriceLineSource.LastVisible,
      priceLineColor: "#37D400",
    });

    newSeries.attachPrimitive(new PartialPriceLine());
    newSeries.setData(historyChart as any);
    // Biến giữ giá trị hiện tại
    let currentValue = historyChart[historyChart.length - 1]; // lưu giá trị mới nhất

    function UpdateData(params: { value: number; time: number }) {
      gsap.to(
        { value: currentValue.value },
        {
          value: params.value,
          duration: 1,
          onUpdate: function () {
            const animatedValue = Number(this.targets()[0].value);

            const now = Math.floor(Date.now() / 1000);

            // timestamp tính theo giây
            newSeries.applyOptions({
              priceLineColor:
                params.value > currentValue.value ? "#37D400" : "#F05",
            });
            // Gửi dữ liệu cập nhật mới nhất
            newSeries.update({
              value: animatedValue,
              time: now as Time,
            });

            // Cập nhật currentValue sau khi update
            currentValue = { value: animatedValue, time: now };
          },
        }
      );
    }

    const unsubscribe = usePrice.subscribe(
      (state) => state.data[pair.pairIndex]?.price,
      (priceStr) => {
        if (!priceStr) return;
        const tradePrice = parseFloat(priceStr);
        if (Number.isNaN(tradePrice)) return;

        UpdateData({
          time: Math.floor(Date.now() / 1000),
          value: tradePrice,
        });
      }
    );

    const handleResize = () => {
      if (!chartContainerRef.current) {
        return;
      }

      chart.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      unsubscribe();
      chart.remove();
    };
  }, [historyChart, pair]);

  if (!pair || isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <span className="loader"></span>
      </div>
    );
  }

  return <div ref={chartContainerRef} className="relative h-[300px]" />;
};

export default Chart;
